import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Button from './Button';
import {ResponsiveContainer, Radar, RadarChart, PolarGrid, PolarRadiusAxis, PolarAngleAxis} from 'recharts';
import styled from 'styled-components';
import {getScrollTop, hexToRGBA, toggleTooltip} from '../lib/utils';

const StyledChartRadar = styled.div`
  float: left;
  position: relative;
  margin-top: 0;
  margin-bottom: 60px;
  height: 200px;
  width: 100%;
  z-index: 1;
  @media (min-width: 380px) {
    height: 300px;
    width: 100%;
  }
  @media (min-width: 540px) {
    height: 400px;
    width: 100%;
  }
  @media (min-width: 1024px) {
    height: 800px;
    width: 90%;
    margin-bottom: 0;
    margin-top: -60px;
  }
  @media (min-width: 1600px) {
    width: 100%;
  }
  > button {
    width: 26px;
    height: auto;
    padding: 0;
    display: block;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -13px);
    z-index: 2;
    > i {
      background-color: #21A0D2;
      width: 26px;
      height: 26px;
      padding: 3px;
      border-radius: 13px;
      margin: 0 auto;
      display: block;
    }
    > span {
      color: #555;
      font-size: 0.625em;
      letter-spacing: 1px;
      line-height: 12px;
      text-align: center;
      text-transform: uppercase;
      display: block;
      margin-top: 4px;
    }
  }

  .recharts-surface {
    overflow: visible;
  }

  .recharts-wrapper tspan {
    font-family: 'Sofia W01 Semi Bold', sans-serif;
    letter-spacing: .83px;
    font-size: .625em;
    text-transform: uppercase;
    text-align: left;
  }

  .recharts-polar-angle-axis polygon,
  .recharts-polar-angle-axis circle,
  .recharts-polar-grid-angle line {
    stroke: #fff;
  }

  /* text {
    transform: translateY(-10px);
    tspan {
      margin: 30px;
    }
  } */

  .recharts-polar-radius-axis-line {
    stroke: #333;
    stroke-width: 1px;
    display: none;
  }

  .recharts-polar-radius-axis-tick text {
    fill: #333;
    transform: translate(-2px, 6px);
    font-size: 1.1em;
  }

  .recharts-polar-grid-angle line {
    stroke-width: 1;
  }

  .recharts-polar-angle-axis-tick-line {
    display: none;
  }

  .recharts-polar-angle-axis tspan {
    fill: #333333;
    font-family: 'Sofia W01 Regular1446825', sans-serif;
    font-size: 6px;
  }

  .recharts-polar-angle-axis-ticks {
    transform: scale(1.05) translate(-2.6%, -1.5%);
  }

  .recharts-polar-grid-concentric-circle {
    fill: url('#shadowTight');
  }

  .recharts-polar-grid-concentric-circle:nth-child(2) {
    fill: url('#shadowWide');
  }

  .recharts-polar-grid-concentric-circle:nth-child(3) {
    fill: url('#shadowNormal');
  }

  .custom-tooltip {
    position: absolute;
    padding: 5px 10px;
    font-size: .625em;
    white-space: nowrap;
    text-transform: uppercase;
    background: #fff;
    box-shadow: 0 0 10px 0 rgba(51, 51, 51, .15);
    pointer-events: none;
    opacity: ${props => props.hover && !props.product ? "1" : "0"};
    -webkit-transition: opacity .2s ease-in-out;
    -moz-transition: opacity .2s ease-in-out;
    -o-transition: opacity .2s ease-in-out;
    transition: opacity .2s ease-in-out;
    z-index: 3;
  }
  @media (min-width: 400px) {
    .recharts-polar-angle-axis tspan {
      font-size: 8px;
    }
  }
  @media (min-width: 640px) {
    .recharts-polar-angle-axis tspan {
      font-size: 10px;
    }
  }
  @media (min-width: 1280px) {
    .recharts-polar-angle-axis tspan {
      font-size: 12px;
    }
  }
`;



export default function ChartRadar (props) {

  const {data, items, product, max, toggleLayer} = props;
  const [mounted, setMounted] = useState();
  const [hover, setHover] = useState();
  const [labels, setLabels] = useState();
  const moveTooltip = (e) =>  {
    const chart = document.getElementsByClassName("chart")[0];
    const x = chart.offsetLeft;
    const y = chart.offsetTop - getScrollTop();
    const pos = `left:${e.clientX - x}px; top:${e.clientY - y}px;color:${!!hover ? hover.color : "#333"};`
    document.getElementsByClassName("custom-tooltip")[0].setAttribute("style", pos);
  }

  useEffect(() => {
    setHover(product);
  }, [product]);

  useEffect(() => {
    const curr = document.querySelectorAll(".recharts-polar-angle-axis-tick-value");
    if(curr.length && data.length){
      curr.forEach(item => {
        item.removeEventListener('mouseenter', toggleTooltip, true);
        item.addEventListener('mouseenter', toggleTooltip, true);
        item.removeEventListener('mouseleave', toggleTooltip, true);
        item.addEventListener('mouseleave', toggleTooltip, true);
      });
      setMounted(true);
    }
  }, [data]);

  return (
    <StyledChartRadar hover={!!hover} product={!!product} onMouseMove={(e) => {moveTooltip(e)}}>
      <ResponsiveContainer>
        {<RadarChart data={data} width={1280} height={1000}>
          <PolarGrid gridType="circle" stroke="#fff" strokeWidth={2} />
          <defs>
           <radialGradient id="shadowTight" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
             <stop offset="85%" style={{stopColor:'rgb(0,0,0)',stopOpacity:'0'}} />
             <stop offset="100%" style={{stopColor:'rgb(0,0,0)',stopOpacity:'.08'}} />
           </radialGradient>
           <radialGradient id="shadowNormal" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
             <stop offset="80%" style={{stopColor:'rgb(0,0,0)',stopOpacity:'0'}} />
             <stop offset="100%" style={{stopColor:'rgb(0,0,0)',stopOpacity:'.08'}} />
           </radialGradient>
           <radialGradient id="shadowWide" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
             <stop offset="75%" style={{stopColor:'rgb(0,0,0)',stopOpacity:'0'}} />
             <stop offset="100%" style={{stopColor:'rgb(0,0,0)',stopOpacity:'.08'}} />
           </radialGradient>
         </defs>
          <PolarAngleAxis dataKey="subject" axisLineType="circle" stroke="#000" />
          <PolarRadiusAxis angle={90} domain={[0, max]} />
          {items.filter(item => item.color).map((item, index) => {
            const active = hover === item;
            return (
              <Radar
                key={index} 
                onMouseEnter={() => setHover(item)} 
                onMouseLeave={() => setHover()}
                name={item.name}
                dataKey={index}
                strokeWidth={index === 0 ? 2 : 1.5}
                stroke={item.color} 
                fill={active ? hexToRGBA(item.color, .5) : `rgba(0,0,0,0)`}
              />
            )
          })}
          {/*<Tooltip isAnimationActive={false} content={<CustomTooltip />}/>*/}
        </RadarChart>}
      </ResponsiveContainer>
      <div className="custom-tooltip">
        <p>{hover ? hover.name : ""}</p>
      </div>
      <Button label="edit" icon="edit" onClick={toggleLayer} transparent/>
    </StyledChartRadar>)
}

ChartRadar.propTypes = {
  data: PropTypes.array,
  size: PropTypes.number,
  axes: PropTypes.bool,
  scales: PropTypes.number,
  captions: PropTypes.object,
  captionMargin: PropTypes.number,
  dots: PropTypes.bool,
  zoomDistance: PropTypes.number,
  smoothing: PropTypes.func,
  setViewBox: PropTypes.func,
  axisProps: PropTypes.func,
  scaleProps: PropTypes.func,
  shapeProps: PropTypes.func,
  captionProps: PropTypes.func,
  dotProps: PropTypes.func
};
